import { DragStartEvent } from '@dnd-kit/core'
import moment from 'moment'

import { SelectOption } from '../components/settings/Options'
import { Bookable } from '../models/Bookable'
import { CalendarFilterType } from '../models/CalendarFilter'
import { Industry } from '../models/Industry'
import { Item } from '../models/Item'
import { User } from '../models/User'
import {
    DATE_ISO_FORMAT,
    DATE_KEY_FORMAT,
    DOW_DATE_FORMAT,
    DOW_MONTH_DAY_FORMAT,
    MONTH_FORMAT,
    MONTH_YEAR_FORMAT,
    SHORT_DATE_FORMAT,
    SHORT_DAY_FORMAT,
    SHORT_DAY_YEAR_FORMAT,
    SHORT_DOW_DATE_FORMAT,
    SHORT_DOW_MONTH_DAY_FORMAT,
    SHORT_MONTH_DAY_FORMAT,
    SHORT_MONTH_YEAR_FORMAT,
    getDaysLeftInMonth,
    isThisMonth,
    isThisYear,
} from './dates'

export const HEADER_BAR_HEIGHT = 36
export const MONTH_HEADER_HEIGHT = 52
export const WEEK_HEADER_HEIGHT = 87
export const DAY_HEADER_HEIGHT = 87
export const ALL_DAY_HEADER_HEIGHT = 100

export const CALENDAR_MARGIN = 24
export const CALENDAR_PADDING = 32

export const DASHBOARD_BANNER_HEIGHT = 84
export const DASHBOARD_BANNER_MARGIN = 24

export const FILTER_ROW_HEIGHT = 28
export const FILTER_ROW_MARGIN = 16

export const TIME_COLUMN_OFFSET = 98
export const MIN_BOOKABLE_WIDTH = 70
export const BOOKABLE_GAP = 4
export const BOOKABLE_PADDING_OFFSET = 8
export const OVERFLOW_BUTTON_OFFSET = 31
export const DAY_MIN_WIDTH = 114

export const PIXELS_PER_MINUTE = 68 / 60

export const calendarViews = [
    'availability',
    'agenda',
    'day',
    '4day',
    'week',
    'month',
    'search',
] as const

export type CalendarView = (typeof calendarViews)[number]

export const getDateLabelForView = (date: moment.Moment, view: CalendarView): string => {
    switch (view) {
        case 'week':
        case 'agenda':
            const startOfWeek = date.clone().startOf('week')
            const endOfWeek = date.clone().endOf('week')

            const startOfWeekLabel = startOfWeek.format(
                isThisYear(startOfWeek) || !isThisYear(endOfWeek)
                    ? SHORT_MONTH_DAY_FORMAT
                    : SHORT_DATE_FORMAT
            )
            const endOfWeekLabel = startOfWeek.isSame(endOfWeek, 'month')
                ? endOfWeek.format(isThisYear(endOfWeek) ? SHORT_DAY_FORMAT : SHORT_DAY_YEAR_FORMAT)
                : endOfWeek.format(
                      isThisYear(endOfWeek) ? SHORT_MONTH_DAY_FORMAT : SHORT_DATE_FORMAT
                  )

            return `${startOfWeekLabel} - ${endOfWeekLabel}`
        case 'month':
            return date.format(isThisYear(date) ? MONTH_FORMAT : MONTH_YEAR_FORMAT)
        case 'availability':
            return date.format(isThisYear(date) ? MONTH_FORMAT : SHORT_MONTH_YEAR_FORMAT)
        case 'day':
        default:
            return date.format(
                isThisYear(date) ? SHORT_DOW_MONTH_DAY_FORMAT : SHORT_DOW_DATE_FORMAT
            )
    }
}

export const getSkipToDateForView = (
    date: moment.Moment,
    view: CalendarView,
    direction: number = 1
): moment.Moment => {
    switch (view) {
        case 'week':
        case 'agenda':
            return date.clone().add(7 * direction, 'days')
        case 'month':
            return date.clone().add(1 * direction, 'month')
        default:
            return date.clone().add(1 * direction, 'days')
    }
}

type CalendarWeek = moment.Moment[]
type CalendarMonth = CalendarWeek[]

export const getCalendarWeekByDate = (date: moment.Moment): CalendarWeek => {
    return Array.from(Array(7).keys()).map((i) => date.clone().startOf('week').add(i, 'day'))
}

export const getCalendar4DaysByDate = (date: moment.Moment): CalendarWeek => {
    return Array.from(Array(4).keys()).map((i) => date.clone().startOf('day').add(i, 'day'))
}

export const getCalendarMonthByDate = (date: moment.Moment): CalendarMonth => {
    const start = date.clone().startOf('month').startOf('week')
    const end = date.clone().endOf('month').endOf('week')

    let month: CalendarMonth = []

    while (start.isBefore(end)) {
        month.push(getCalendarWeekByDate(start))
        start.add(7, 'day')
    }

    return month
}

export const getCalendarStartAndEnd = (
    date: moment.Moment,
    view: CalendarView
): { start: moment.Moment; end: moment.Moment } => {
    let start: moment.Moment, end: moment.Moment

    switch (view) {
        case 'week':
        case 'agenda':
            start = date.clone().startOf('week')
            end = date.clone().endOf('week')
            break
        case 'month':
            start = date.clone().startOf('month').startOf('week')
            end = date.clone().endOf('month').endOf('week')
            break
        case '4day':
            start = date.clone().startOf('day')
            end = date.clone().add(3, 'days').endOf('day')
            break
        case 'day':
        case 'availability':
        default:
            start = date.clone().startOf('day')
            end = date.clone().endOf('day')
            break
    }

    return {
        start,
        end,
    }
}

export const getSingularType = (type: CalendarFilterType) =>
    type === 'classes' ? type.slice(0, -2) : type.slice(0, -1)

export type CalendarDragStartEvent = DragStartEvent & {
    active: { data: { current: { bookable: Bookable } } }
}

export type CalendarDragState = {
    top: number
    offset: number
    state: '' | 'start' | 'move' | 'end' | 'cancel'
    date: moment.Moment | undefined
    bookable: Bookable | undefined
}

export const initialDragState: CalendarDragState = {
    top: -1,
    offset: 0,
    state: '',
    date: undefined,
    bookable: undefined,
}

export type AvailabiltySearchParams = {
    delta?: number
    start?: moment.Moment | string
    duration?: number
    vendor?: string
    employee?: string | string[]
    item?: string
    itemType?: string
}

export type AvailabilitySearchOptions = {
    date: moment.Moment
    filter: AvailabilityFilter
    items: Item[]
    industry: Industry
    vendor: string
}

export const getAvailabilitySearchParams = (
    options: AvailabilitySearchOptions
): AvailabiltySearchParams => {
    const { date, filter, items, industry, vendor } = options
    const { pros, services } = filter
    const employee = pros.length > 0 ? { employee: pros } : {}
    const item = services.length > 0 ? { item: services[0] } : {}

    const itemType =
        services.length > 0 && services[0] in items
            ? items[services[0] as any]?.type
            : filter.duration.value >= 1440
              ? 'reservation'
              : 'service'

    const duration =
        itemType === 'service'
            ? { duration: filter.duration.value || industry.duration }
            : !!filter.duration.value
              ? { duration: filter.duration.value }
              : {}

    const params = {
        delta: isThisMonth(date) ? getDaysLeftInMonth(moment()) : date.daysInMonth(),
        start: date.format(DATE_ISO_FORMAT),
        vendor,
        itemType,
    }

    return { ...params, ...duration, ...employee, ...item }
}

export type CalendarAvailabilityLocationState = {
    date: string
    item: Item | null | undefined
    proIds: string[]
    duration: number
}

export type CalendarAvailability = {
    date: string
    duration: number
    start: string
    end: string
    available: boolean
    employees?: User[]
    annotation?: string
}

export type AvailabilityByHash = { [hash: string]: CalendarAvailability[] }
export type AvailabilityByDate = { [date: string]: CalendarAvailability[] }

export const getAvailabilityByDate = (availability: CalendarAvailability[]): AvailabilityByDate => {
    return availability.reduce(
        (availabilityByDate: AvailabilityByDate, a: CalendarAvailability) => {
            const date = moment(a.date).format(DATE_KEY_FORMAT)
            const dateAvailability = availabilityByDate[date] || []

            return {
                ...availabilityByDate,
                [date]: dateAvailability.concat(a),
            }
        },
        {}
    )
}

export const getDateLabelForAvailability = (
    date: moment.Moment,
    duration: number,
    shortFormat = false
): string => {
    const defaultLabel = shortFormat
        ? date.format(isThisYear(date) ? SHORT_DOW_MONTH_DAY_FORMAT : SHORT_DOW_DATE_FORMAT)
        : date.format(isThisYear(date) ? DOW_MONTH_DAY_FORMAT : DOW_DATE_FORMAT)

    if (duration >= 1440) {
        const start = date.clone()
        const end = start.clone().add(duration, 'minutes')
        const startLabel = start.format(
            isThisYear(start) || !isThisYear(end) ? SHORT_MONTH_DAY_FORMAT : SHORT_DATE_FORMAT
        )
        const endLabel = start.isSame(end, 'month')
            ? end.format(isThisYear(end) ? SHORT_DAY_FORMAT : SHORT_DAY_YEAR_FORMAT)
            : end.format(isThisYear(end) ? SHORT_MONTH_DAY_FORMAT : SHORT_DATE_FORMAT)

        return `${startLabel}-${endLabel}`
    }

    return defaultLabel
}

export type TimeSlot = {
    start: moment.Moment
    time: string
    duration: number
    available: boolean
    employees: User[]
    annotation?: string // TODO: annotation will be replaced with remaining_capacity?: number
}

type TimeSlotsByDaypart = {
    morning?: TimeSlot[]
    afternoon?: TimeSlot[]
    evening?: TimeSlot[]
}

type TimeSlotsByGroup = TimeSlotsByDaypart & { [k: string]: TimeSlot[] }

export const getAvailabilityTimeSlots = (
    availability: CalendarAvailability[],
    day: moment.Moment,
    itemType: string
): TimeSlotsByGroup => {
    const slots: TimeSlot[] = availability.map((a) => ({
        start: moment(a.start),
        time: moment(a.start).format('h:mm A'),
        duration: a.duration,
        available: a.available,
        employees: a.employees ?? [],
        annotation: a.annotation ?? '', // TODO: annotation will be replaced with remaining_capacity
    }))

    if (['class', 'reservation'].includes(itemType))
        return slots.length > 0 ? { [itemType]: slots } : {}

    return slots.reduce((slots: TimeSlotsByDaypart, slot: TimeSlot) => {
        if (slot.start.isBefore(day.clone().startOf('day').add(12, 'hours'))) {
            slots.morning = !slots.morning ? [slot] : [...slots.morning, slot]
        }
        else if (slot.start.isBefore(day.clone().startOf('day').add(18, 'hours'))) {
            slots.afternoon = !slots.afternoon ? [slot] : [...slots.afternoon, slot]
        }
        else {
            slots.evening = !slots.evening ? [slot] : [...slots.evening, slot]
        }

        return slots
    }, {})
}

export type DurationSelection = {
    hours: SelectOption
    minutes: SelectOption
    days: SelectOption
    weeks: SelectOption
}

export const emptyDurationSelection = {
    hours: { value: '', label: '' },
    minutes: { value: '', label: '' },
    days: { value: '', label: '' },
    weeks: { value: '', label: '' },
}

export type Duration = {
    value: number
    label: string
}

export type AvailabilityFilter = {
    pros: string[]
    services: string[]
    duration: Duration
}

export const emptyAvailabilityFilter: AvailabilityFilter = {
    pros: [],
    services: [],
    duration: { value: 0, label: 'Duration' },
}

export const getDurationFromDurationSelection = (
    durationSelection: DurationSelection
): Duration => {
    const { hours, minutes, days, weeks } = durationSelection

    const value = +weeks.value + +days.value + +hours.value + +minutes.value
    const hourString = hours.value && minutes.value ? `${hours.label},` : hours.label
    const weekString = weeks.value && days.value ? `${weeks.label},` : weeks.label
    const label =
        (minutes.value || hours.value
            ? `${hourString} ${minutes.label}`
            : `${weekString} ${days.label}`
        ).trim() || 'Duration'

    return { value, label }
}

export const getDurationSelectionFromDurationValue = (value: number): DurationSelection => {
    const w = Math.floor(value / (7 * 24 * 60))
    const d = Math.floor(value / (24 * 60)) % 7
    const h = Math.floor(value / 60) % 24
    const m = value % 60

    return {
        weeks:
            w > 0
                ? {
                      value: (w * 7 * 24 * 60).toString(),
                      label: `${w} ${w === 1 ? 'week' : 'weeks'}`,
                  }
                : emptyDurationSelection.weeks,
        days:
            d > 0
                ? { value: (d * 24 * 60).toString(), label: `${d} ${d === 1 ? 'day' : 'days'}` }
                : emptyDurationSelection.days,
        hours:
            h > 0
                ? { value: (h * 60).toString(), label: `${h} ${h === 1 ? 'hour' : 'hours'}` }
                : emptyDurationSelection.hours,
        minutes:
            m > 0
                ? { value: m.toString(), label: `${m} ${m === 1 ? 'minute' : 'minutes'}` }
                : emptyDurationSelection.minutes,
    }
}

export const getDurationFromDurationValue = (value: number): Duration =>
    getDurationFromDurationSelection(getDurationSelectionFromDurationValue(value))

export const getUserStartHours = (user: User, defaultStart: number = 10): number[] => {
    return [
        user.sunStartHour || defaultStart,
        user.monStartHour || defaultStart,
        user.tueStartHour || defaultStart,
        user.wedStartHour || defaultStart,
        user.thuStartHour || defaultStart,
        user.friStartHour || defaultStart,
        user.satStartHour || defaultStart,
    ]
}
